import { useRef } from "react";
import { SecondaryButton } from "../Button/Buttons";
import { validateFileOnCreateSpectra, ALLOWED_FILE_ACCEPT_STRING } from "../../utils/helpers";
import { SPECTRA_MAX_FILE_COUNT, SPECTRA_MAX_TOTAL_SIZE_MB, SPECTRA_MAX_SINGLE_FILE_SIZE_MB } from "../../constants/fileConstants";
import { BlackCross, CrossSvg } from "../../assets";
import { useTheme } from "../../context/ThemeProvider";
import { v4 as uuidv4 } from "uuid";

const FilePreview = ({ fileObj, index, onCancel }) => {
  const { theme } = useTheme();
  const { file, progress, isUploaded } = fileObj;

  const getUploadStatus = () => {
    if (isUploaded) return "✓ Uploaded";
    if (progress > 0) return `${Math.round(progress)}%`;
    return "Queued";
  };

  return (
    <div
      className={`
              flex items-center gap-2 px-3 border-[0.5px] py-1 rounded-full min-w-fit text-xs
              ${isUploaded
                ? 'bg-fill-green-50 border-edge-green-200 dark:bg-fill-green-900/20 dark:border-edge-green-600'
                : 'bg-fill-navigation-hover border-edge-hex-6d808d4d dark:bg-fill-hex-454e5a dark:border-edge-control-border'
              } opacity-100
              relative overflow-hidden shadow-[0_0_2px_#66666626] dark:shadow-[0_0_4px_#00000040]
            `}
    >
      {/* Filename and status */}
      <div className="flex items-center gap-2">
        <span className="text-xs max-w-[120px] truncate font-semibold text-ink-ink-alternate dark:text-ink-sidebar-text-dark opacity-100">
          {file.name}
        </span>
        <span className={`text-xs font-medium ${
          isUploaded
            ? 'text-ink-green-600 dark:text-ink-green-400'
            : 'text-ink-orange-600 dark:text-ink-hex-fb923c'
        }`}>
          {getUploadStatus()}
        </span>
      </div>

      <button
        onClick={() => onCancel(index)}
        className="text-ink-gray-400 hover:text-ink-gray-300 transition-colors flex-shrink-0"
        aria-label={`Remove ${file.name}`}
      >
        {theme === "dark" ? (
          <CrossSvg className="h-4" />
        ) : (
          <BlackCross className="h-4" />
        )}
      </button>

      {/* Progress line at bottom - with fixed position */}
      {!isUploaded && progress > 0 && progress < 100 && (
          <div
            className="absolute bottom-0 left-0 right-0 h-[1px] bg-fill-editor-warning origin-left transition-transform duration-300 ease-in-out"
            style={{
              transform: `scaleX(${progress / 100})`,
            }}
          />
        )}
    </div>
  );
};

const FileUploadInSpectraCreation = ({ files, setFiles }) => {
  const inputRef = useRef(null);

  const handleAddFilesBtnClick = () => {
    if (inputRef.current) {
      inputRef.current.click();
    }
  };

  // const handleFileChange = (e) => {
  //   const file = e.target?.files?.[0];
  //   if (validateFileOnCreateSpectra(file, files, 7, 5, 7)) {
  //     const file_path = getFilePath(file);
  //     const storageRef = ref(EnvStore.getState().storage, file_path);
  //     const uploadTask = uploadBytesResumable(storageRef, file, {
  //       chunkSize: 256 * 1024,
  //     });

  //     const fileObj = {
  //       file,
  //       file_path,
  //       isUploaded: false,
  //       progress: 0,
  //       uploadTask,
  //     };

  //     setFiles((prevFiles) => [...prevFiles, fileObj]);

  //     uploadTask.on(
  //       "state_changed",
  //       (snapshot) => {
  //         const progress = Math.round(
  //           (snapshot.bytesTransferred / snapshot.totalBytes) * 100
  //         );
  //         setFiles((prev) =>
  //           prev.map((f, i) =>
  //             f.file.name === file.name
  //               ? { ...f, progress: mapValue(progress, 0, 100, 0, 90) }
  //               : f
  //           )
  //         );
  //       },
  //       (error) => {
  //         setFiles((prev) => prev.filter((f) => f.file.name !== file.name));
  //       },
  //       () => {
  //         getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
  //           setFiles((prev) =>
  //             prev.map((f) =>
  //               f.file.name === file.name
  //                 ? { ...f, isUploaded: true, downloadURL, progress: 100 }
  //                 : f
  //             )
  //           );
  //         });
  //       }
  //     );
  //   }
  // };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target?.files || []);
    const validFiles = [];
    selectedFiles.forEach((file) => {
      const pendingFiles = validFiles.map((pendingFile) => ({ file: pendingFile }));
      if (validateFileOnCreateSpectra(file, [...files, ...pendingFiles], SPECTRA_MAX_SINGLE_FILE_SIZE_MB, SPECTRA_MAX_FILE_COUNT, SPECTRA_MAX_TOTAL_SIZE_MB)) {
        validFiles.push(file);
      }
    });
    const newFileObjects = validFiles.map((file) => {
      const uploadKey = uuidv4();
      return {
        file,
        uploadKey,
        uploadId: uploadKey,
        isUploaded: false,
        progress: 0,
      };
    });
    setFiles((prevFiles) => [...prevFiles, ...newFileObjects]);
  };

  const handleCancel = async (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
    inputRef.current.value = "";
  };

  return (
    <>
      <label htmlFor="add-files-on-create-spectra" className="cursor-pointer">
        <span
          className={`text-ink-ink dark:text-ink-white text-sm font-semibold`}
        >
          Add Attachments
        </span>
        <span className="text-ink-hex-aaaaaa text-xs block mb-2">
          Upload up to {SPECTRA_MAX_FILE_COUNT} files, maximum upload size {SPECTRA_MAX_SINGLE_FILE_SIZE_MB >= 1024 ? `${SPECTRA_MAX_SINGLE_FILE_SIZE_MB / 1024} GB` : `${SPECTRA_MAX_SINGLE_FILE_SIZE_MB} MB`} per file.
        </span>
      </label>
      {files.length > 0 && (
        <div className="flex items-center flex-wrap gap-2 mb-2">
          {files.map((fileObj, index) => (
            <FilePreview
              key={index}
              fileObj={fileObj}
              index={index}
              onCancel={handleCancel}
            />
          ))}
        </div>
      )}
      <div>
        <SecondaryButton type="button" onClick={handleAddFilesBtnClick}>
          Add Files
        </SecondaryButton>
      </div>
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        accept={ALLOWED_FILE_ACCEPT_STRING}
        id="add-files-on-create-spectra"
        onChange={handleFileChange}
        multiple // Add this attribute
      />
    </>
  );
};

export default FileUploadInSpectraCreation;
